import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Danger, Refresh2, Home2, ArrowLeft2, MessageQuestion } from "iconsax-react";
import { stashErrorReport } from "@/shared/support";
import { Button } from "@/shared/ui";
import SendErrorReportButton from "../SendErrorReportButton";

interface Props {
  error?: Error | null;
}

export default function ErrorFallback({ error }: Props) {
  const { t } = useTranslation("ErrorBoundary");
  const [sendFailed, setSendFailed] = useState(false);

  function handleReload() {
    window.location.reload();
  }

  function handleGoHome() {
    window.location.assign("/");
  }

  function handleGoBack() {
    window.history.back();
  }

  function handleContactSupport() {
    const pagePath = window.location.pathname;
    stashErrorReport(error ?? null, pagePath);
    window.location.assign(`/support?from=${encodeURIComponent(pagePath)}`);
  }

  return (
    <>
      <title>{t("meta.title")}</title>
      <main className="min-h-screen w-full flex items-center justify-center p-4 surface-secondary-fill">
        <div className="w-full max-w-md flex flex-col items-center text-center gap-5 p-8 radius-3xl surface-base-fill elevation-level-1">
          <span className="w-16 h-16 radius-full surface-notice-subtle flex items-center justify-center content-action-notice">
            <Danger size={32} color="currentColor" variant="Bold" />
          </span>
          <div className="flex flex-col gap-2">
            <h1 className="text-display-xs content-base-primary">{t("title")}</h1>
            <p className="text-body-regular-md content-base-secondary">{t("description")}</p>
            <p className="text-body-regular-sm content-base-low">{t("contact")}</p>
          </div>
          <div className="flex flex-col gap-3 w-full">
            <Button variant="primary" size="lg" onClick={handleReload}>
              <Refresh2 size={16} color="currentColor" />
              {t("reload")}
            </Button>
            <SendErrorReportButton error={error} size="lg" onFailure={() => setSendFailed(true)} />
            {sendFailed && (
              <Button variant="tertiary" size="lg" onClick={handleContactSupport}>
                <MessageQuestion size={16} color="currentColor" />
                {t("contactSupport")}
              </Button>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Button variant="secondary" size="lg" onClick={handleGoHome}>
                <Home2 size={16} color="currentColor" />
                {t("goHome")}
              </Button>
              <Button variant="tertiary" size="lg" onClick={handleGoBack}>
                <ArrowLeft2 size={16} color="currentColor" />
                {t("goBack")}
              </Button>
            </div>
          </div>
          {import.meta.env.DEV && error && (
            <details className="w-full text-left mt-1">
              <summary className="text-body-regular-sm content-base-secondary cursor-pointer">
                {t("technicalDetails")}
              </summary>
              <pre className="mt-2 p-3 radius-md surface-secondary-fill border surface-base-stroke text-body-regular-xs content-base-secondary overflow-auto max-h-64 whitespace-pre-wrap font-mono">
                {error.stack || error.message}
              </pre>
            </details>
          )}
        </div>
      </main>
    </>
  );
}
